import type { EntityId, Vec2 } from "../types";

type BodyGroup = "ship" | "asteroid" | "item" | "projectile" | "particle";
type BodyKind = "rigid" | "point" | "particle";

/** cx/cy/rot in body-CoM local; body rot applied on top. */
export interface SubCollider {
  hw: number;
  hh: number;
  cx: number;
  cy: number;
  rot: number;
  alive: boolean;
  ref: unknown;
}

export interface BodyHandle {
  id: EntityId;
  /** Dense per-step index into the broadphase handle list. Used as the
   *  pair-dedup key so it stays bounded — entity ids grow unbounded over a
   *  session and would overflow the packed key. Assigned each physics step. */
  idx: number;
  kind: BodyKind;
  group: BodyGroup;
  ref: unknown;

  pos: Vec2;
  /** Pose at start of step, for swept broadphase + CCD. */
  prevPos: Vec2;
  vel: Vec2;
  rot: number;
  prevRot: number;
  angVel: number;

  mass: number;
  /** 0 for point/particle. */
  inertia: number;

  restitution: number;
  toughness: number;
  tempK: number;
  heatCap: number;

  pointRadius: number;
  subs: SubCollider[];
  radius: number;

  /** -1 = no owner. */
  owner: EntityId;
  /** Welded/docked pairs never contact. */
  connected: ReadonlySet<EntityId> | null;
  noSelfContact: boolean;

  /** Tick fraction `pos` represents; 1 = end-of-tick. Swept CCD multiplies in. */
  toi: number;

  dead: boolean;
}

/** nx/ny points A → B. */
export interface Contact {
  a: BodyHandle;
  b: BodyHandle;
  cx: number;
  cy: number;
  nx: number;
  ny: number;
  depth: number;
  /** + = closing along normal at contact. */
  vClosing: number;
  subA: SubCollider | null;
  subB: SubCollider | null;
}

export const GRID_CELL_M = 32;

const PAIR_ID_BASE = 1 << 24;
export function pairKey(a: number, b: number): number {
  return a < b ? a * PAIR_ID_BASE + b : b * PAIR_ID_BASE + a;
}
